import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import apiService from "../../services/api/api.service";
import { Loader, Search, ChevronRight, TrendingUp, Heart } from "lucide-react";
import { useScreener } from "../../contexts/notification/ScreenerContext";
import { useWatchlist } from "../../services/contextAPi/watchlistContext";
import { toast } from "react-hot-toast";

const ScreenerList = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedItems, setHighlightedItems] = useState(new Set());
  const [productIds, setProductIds] = useState({}); // Map uniqueTitle to productId
  const [watchlistLoading, setWatchlistLoading] = useState(new Set()); // Track loading state per product
  const navigate = useNavigate();
  const { shouldSortByRecent, highlightTop3 } = useScreener();
  const { watchlists, isProductInWatchlist, addToWatchlist, removeFromWatchlist, createWatchlist } = useWatchlist();

  const fetchCompanies = async (sortByRecent = false) => {
    try {
      setLoading(true);
      const response = await apiService.getScreenerData(sortByRecent);

      const filteredCompanies = response.data
        ?.filter(
          (company) =>
            typeof company.basicData?.legalName === "string" &&
            !/^Sat\s/.test(company.basicData.legalName) &&
            company.basicData.legalName.length > 5
        )
        .map((company) => ({
          name: company.basicData.legalName,
          uniqueTitle: company.basicData.uniqueTitle,
          sector: company.basicData?.sector || "N/A",
          productId: company._id, // Store the product ID directly from the screener data
          logo: company.logo || company.basicData?.logo || null,
        }));

      setCompanies(filteredCompanies || []);

      // Create a mapping of uniqueTitle to productId
      const idMapping = {};
      filteredCompanies?.forEach(company => {
        if (company.uniqueTitle && company.productId) {
          idMapping[company.uniqueTitle] = company.productId;
        }
      });
      setProductIds(idMapping);

      if (sortByRecent && filteredCompanies?.length > 0) {
        const top3Indices = new Set(
          [0, 1, 2].filter((i) => i < filteredCompanies.length)
        );
        setHighlightedItems(top3Indices);
      } else {
        setHighlightedItems(new Set());
      }
    } catch (err) {
      console.error("Error fetching screener data:", err.message);
      setError("Failed to fetch company list.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (shouldSortByRecent) {
      fetchCompanies(true);
    }
  }, [shouldSortByRecent]);

  useEffect(() => {
    if (!highlightTop3) {
      setHighlightedItems(new Set());
    }
  }, [highlightTop3]);

  const handleRedirect = (uniqueTitle) => {
    if (!uniqueTitle) {
      console.error("Missing uniqueTitle for navigation");
      return;
    }
    navigate(`/company_info/${uniqueTitle}`);
  };

  const toggleWatchlist = async (e, uniqueTitle) => {
    e.stopPropagation();

    const productId = productIds[uniqueTitle];
    if (!productId) {
      toast.error("Product ID not found");
      return;
    }

    // Check if already processing this product
    if (watchlistLoading.has(uniqueTitle)) {
      return;
    }

    // Add to loading set
    setWatchlistLoading(prev => new Set([...prev, uniqueTitle]));

    try {
      const isInWatchlist = isProductInWatchlist(productId);

      if (isInWatchlist) {
        // Find the watchlist containing this product
        const watchlistWithProduct = watchlists.find(wl =>
          wl.products?.some(p => (p.productId?._id || p.productId) === productId)
        );

        if (watchlistWithProduct) {
          await removeFromWatchlist(watchlistWithProduct._id, productId);
          toast.success("Removed from watchlist");
        }
      } else {
        // Get or create a default watchlist
        let defaultWatchlist = watchlists.find(wl => wl.name === "My Watchlist");

        if (!defaultWatchlist && watchlists.length > 0) {
          // Use the first available watchlist
          defaultWatchlist = watchlists[0];
        } else if (!defaultWatchlist) {
          // Create a new default watchlist
          const createResponse = await createWatchlist({
            name: "My Watchlist",
            description: "My default watchlist",
            isPublic: false
          });

          if (createResponse && createResponse.success) {
            defaultWatchlist = createResponse.data;
          } else {
            throw new Error("Failed to create watchlist");
          }
        }

        if (defaultWatchlist) {
          await addToWatchlist(defaultWatchlist._id, productId);
          toast.success("Added to watchlist");
        }
      }
    } catch (error) {
      console.error("Error toggling watchlist:", error);
      toast.error(error.message || "Failed to update watchlist");
    } finally {
      // Remove from loading set
      setWatchlistLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(uniqueTitle);
        return newSet;
      });
    }
  };

  const filteredList = useMemo(() => {
    if (!searchTerm) return companies;
    return companies.filter(company =>
      company.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [companies, searchTerm]);

  if (loading)
    return (
      <div className="flex justify-center items-center h-48 bg-white rounded-xl shadow-sm border border-gray-100">
        <Loader className="animate-spin text-[#44D2F6]" size={24} />
      </div>
    );

  if (error)
    return (
      <div className="p-4 bg-red-50 text-red-500 rounded-xl text-sm border border-red-100">
        {error}
      </div>
    );

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col h-full">
      <div className="p-4 border-b border-gray-100 bg-white sticky top-0 z-10">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold text-[#102147] flex items-center text-sm">
            <TrendingUp className="w-4 h-4 mr-2 text-[#0C7FA5]" />
            Market List
          </h3>
          <Link to="/screener" className="text-xs font-medium text-[#0C7FA5] hover:text-[#102147] transition-colors">
            View All
          </Link>
        </div>
        <div className="relative group">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 group-focus-within:text-[#44D2F6] transition-colors" />
          <input
            type="text"
            placeholder="Search companies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#44D2F6]/20 focus:border-[#44D2F6] transition-all placeholder:text-gray-400"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-0.5 custom-scrollbar">
        {filteredList.length > 0 ? (
          filteredList.map((company, index) => (
            <div
              key={index}
              onClick={() => handleRedirect(company.uniqueTitle)}
              className={`group relative flex items-center justify-between p-3 mb-2 rounded-xl border transition-all duration-300 cursor-pointer ${highlightedItems.has(index)
                ? "bg-blue-50/40 border-blue-100 shadow-sm"
                : "bg-white border-gray-50 hover:border-blue-100 hover:shadow-md hover:-translate-y-0.5"
                }`}
            >
              <div className="flex-1 min-w-0 mr-3 flex items-center">
                {/* Logo or Fallback Avatar */}
                <div className="w-10 h-10 flex-shrink-0 rounded-lg overflow-hidden border border-gray-100 bg-gradient-to-br from-[#44D2F6]/20 to-[#0C7FA5]/20 flex items-center justify-center text-[#0C7FA5] font-bold text-lg mr-3 shadow-sm group-hover:shadow transition-all group-hover:scale-105">
                  {company.logo ? (
                    <img
                      src={company.logo}
                      alt={company.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null; // Prevent infinite loop if fallback fails
                        e.target.style.display = 'none'; // Hide broken image
                        e.target.nextSibling.style.display = 'flex'; // Show text initials
                      }}
                    />
                  ) : null}
                  <div className={company.logo ? "hidden" : "w-full h-full flex items-center justify-center"}>
                    {company.name.charAt(0).toUpperCase()}
                  </div>
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between mb-0.5">
                    <h4 className={`text-sm font-semibold truncate transition-colors ${highlightedItems.has(index) ? "text-[#0C7FA5]" : "text-gray-800 group-hover:text-[#0C7FA5]"
                      }`}>
                      {company.name}
                    </h4>
                  </div>
                  <div className="flex items-center text-xs text-gray-500">
                    <span className="truncate max-w-[120px]">{company.sector}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-1">
                <button
                  onClick={(e) => toggleWatchlist(e, company.uniqueTitle)}
                  disabled={watchlistLoading.has(company.uniqueTitle)}
                  className={`p-2 rounded-full transition-all duration-200 transform active:scale-90 disabled:opacity-50 disabled:cursor-not-allowed ${productIds[company.uniqueTitle] && isProductInWatchlist(productIds[company.uniqueTitle])
                    ? "text-red-500 bg-red-50 hover:bg-red-100 shadow-sm"
                    : "text-gray-300 hover:text-red-500 hover:bg-red-50 hover:shadow-sm"
                    }`}
                  title={
                    productIds[company.uniqueTitle] && isProductInWatchlist(productIds[company.uniqueTitle])
                      ? "Remove from Watchlist"
                      : "Add to Watchlist"
                  }
                >
                  {watchlistLoading.has(company.uniqueTitle) ? (
                    <Loader className="animate-spin" size={16} />
                  ) : (
                    <Heart
                      size={16}
                      strokeWidth={1.5}
                      fill={
                        productIds[company.uniqueTitle] && isProductInWatchlist(productIds[company.uniqueTitle])
                          ? "currentColor"
                          : "none"
                      }
                    />
                  )}
                </button>

                <div className={`p-1.5 rounded-full transition-colors ${highlightedItems.has(index) ? "text-[#0C7FA5]" : "text-gray-300 group-hover:text-[#0C7FA5]"
                  }`}>
                  <ChevronRight size={16} strokeWidth={1.5} />
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="p-8 text-center">
            <p className="text-xs text-gray-400">No companies found</p>
          </div>
        )}
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #e2e8f0;
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #cbd5e1;
        }
      `}</style>
    </div>
  );
};

export default ScreenerList;
